import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, Video, ArrowRight } from 'lucide-react';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import brandLogo from '../../assets/brand/logo-transparent.png';

const AppointmentSection = () => {
  const { t } = usePublicLanguage();

  const providers = [
    {
      name: 'Microsoft Teams',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" className="w-16 h-16" viewBox="0 0 48 48">
          <path fill="#5c6bc0" d="M41.5 13A3.5 3.5 0 1 0 41.5 20 3.5 3.5 0 1 0 41.5 13zM4 40l23 4V4L4 8V40z"></path>
          <path fill="#fff" d="M21 16.27L21 19 17.01 19.18 16.99 31.04 14.01 30.95 14.01 19.29 10 19.45 10 16.94z"></path>
          <path fill="#5c6bc0" d="M36 14c0 2.21-1.79 4-4 4-1.2 0-2.27-.53-3-1.36v-5.28c.73-.83 1.8-1.36 3-1.36C34.21 10 36 11.79 36 14zM38 23v11c0 0 1.567 0 3.5 0 1.762 0 3.205-1.306 3.45-3H45v-8H38zM29 20v17c0 0 1.567 0 3.5 0 1.762 0 3.205-1.306 3.45-3H36V20H29z"></path>
        </svg>
      ),
      color: 'from-[#5059C9] to-[#7B83EB]'
    },
    {
      name: 'Zoom',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" className="w-16 h-16" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="20" fill="#2196f3"></circle>
          <path fill="#fff" d="M29,31H14c-1.657,0-3-1.343-3-3V17h15c1.657,0,3,1.343,3,3V31z"></path>
          <polygon fill="#fff" points="37,31 31,27 31,21 37,17"></polygon>
        </svg>
      ),
      color: 'from-[#2D8CFF] to-[#0B5CFF]'
    },
    {
      name: 'Google Meet',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" className="w-16 h-16" viewBox="0 0 48 48">
          <rect width="16" height="16" x="12" y="16" fill="#fff" transform="rotate(-90 20 24)"></rect>
          <polygon fill="#1e88e5" points="3,17 3,31 8,32 13,31 13,17 8,16"></polygon>
          <path fill="#4caf50" d="M37,24v14c0,1.657-1.343,3-3,3H13l-1-5l1-5h14v-7l5-1L37,24z"></path>
          <path fill="#fbc02d" d="M37,10v14H27v-7H13l-1-5l1-5h21C35.657,7,37,8.343,37,10z"></path>
          <path fill="#1565c0" d="M13,31v10H6c-1.657,0-3-1.343-3-3v-7H13z"></path>
          <polygon fill="#e53935" points="13,7 13,17 3,17"></polygon>
          <polygon fill="#2e7d32" points="38,24 37,32.45 27,24 37,15.55"></polygon>
          <path fill="#4caf50" d="M46,10.11v27.78c0,0.84-0.98,1.31-1.63,0.78L37,32.45v-16.9l7.37-6.22C45.02,8.8,46,9.27,46,10.11z"></path>
        </svg>
      ),
      color: 'from-[#1A73E8] to-[#188038]'
    },
    {
      name: 'Slack',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" className="w-16 h-16" viewBox="0 0 40 40">
          <path fill="#8cd3df" d="M4.342,21.046c-1.208,0-2.286-0.767-2.683-1.909c-0.25-0.717-0.205-1.488,0.126-2.171 c0.33-0.683,0.907-1.196,1.624-1.446l27.305-9.496c0.303-0.105,0.616-0.159,0.934-0.159c1.207,0,2.286,0.767,2.683,1.909 c0.25,0.717,0.205,1.488-0.126,2.171c-0.33,0.683-0.907,1.196-1.624,1.446L5.275,20.888C4.973,20.993,4.659,21.046,4.342,21.046z"></path>
          <path fill="#5f9099" d="M31.647,6.366L31.647,6.366c0.995,0,1.884,0.632,2.211,1.573c0.424,1.219-0.223,2.556-1.442,2.98 L5.111,20.416c-0.25,0.087-0.508,0.131-0.769,0.131c-0.995,0-1.884-0.632-2.211-1.573c-0.424-1.219,0.223-2.556,1.442-2.98 l27.305-9.496C31.127,6.41,31.386,6.366,31.647,6.366 M31.647,5.366c-0.364,0-0.734,0.06-1.098,0.186L3.245,15.048 c-1.743,0.606-2.665,2.51-2.058,4.253c0.48,1.38,1.773,2.245,3.156,2.245c0.364,0,0.734-0.06,1.098-0.186l27.305-9.496 c1.743-0.606,2.664-2.51,2.058-4.253C34.323,6.231,33.03,5.366,31.647,5.366L31.647,5.366z"></path>
          <path fill="#dd215a" d="M8.353,34.134c-1.207,0-2.286-0.767-2.683-1.909c-0.25-0.717-0.205-1.488,0.126-2.171 c0.33-0.683,0.907-1.196,1.624-1.446l27.305-9.496c0.303-0.105,0.616-0.158,0.934-0.158c1.208,0,2.286,0.767,2.683,1.909 c0.25,0.717,0.205,1.488-0.126,2.171c-0.33,0.683-0.907,1.196-1.624,1.446L9.286,33.975C8.983,34.08,8.67,34.134,8.353,34.134z"></path>
          <path fill="#a81945" d="M35.658,19.454L35.658,19.454c0.995,0,1.884,0.632,2.211,1.573c0.424,1.219-0.223,2.556-1.442,2.98 L9.122,33.503c-0.25,0.087-0.508,0.131-0.769,0.131c-0.995,0-1.884-0.632-2.211-1.573c-0.424-1.219,0.223-2.556,1.442-2.98 l27.305-9.496C35.138,19.498,35.397,19.454,35.658,19.454 M35.658,18.454c-0.364,0-0.734,0.06-1.098,0.186L7.256,28.136 c-1.743,0.606-2.664,2.51-2.058,4.253v0c0.48,1.38,1.773,2.245,3.156,2.245c0.364,0,0.734-0.06,1.098-0.186l27.305-9.496 c1.743-0.606,2.665-2.51,2.058-4.253C38.334,19.318,37.041,18.453,35.658,18.454L35.658,18.454z"></path>
          <path fill="#ebb23d" d="M31.291,34.489c-1.208,0-2.286-0.767-2.683-1.909L19.112,5.276c-0.515-1.48,0.271-3.103,1.75-3.617 C21.165,1.554,21.479,1.5,21.796,1.5c1.208,0,2.287,0.767,2.684,1.909l9.495,27.304c0.25,0.717,0.205,1.488-0.126,2.171 c-0.33,0.683-0.907,1.196-1.624,1.446C31.922,34.436,31.608,34.489,31.291,34.489z"></path>
          <path fill="#b58a2f" d="M21.796,2L21.796,2c0.995,0,1.884,0.632,2.211,1.573l9.496,27.305 c0.424,1.219-0.223,2.556-1.442,2.98c-0.25,0.087-0.508,0.131-0.769,0.131c-0.995,0-1.884-0.632-2.211-1.573L19.584,5.111 c-0.205-0.591-0.169-1.226,0.104-1.789c0.272-0.563,0.748-0.986,1.339-1.192C21.276,2.044,21.535,2,21.796,2 M21.796,1 c-0.364,0-0.734,0.06-1.098,0.186c-1.743,0.606-2.665,2.51-2.058,4.253l9.496,27.305c0.48,1.38,1.773,2.245,3.156,2.245 c0.364,0,0.734-0.06,1.098-0.186c1.743-0.606,2.665-2.51,2.058-4.253L24.952,3.245C24.472,1.865,23.179,1,21.796,1L21.796,1z"></path>
          <path fill="#cd171d" d="M25.865 21.365H31.548V27.046999999999997H25.865z" transform="rotate(-19.178 28.706 24.206)"></path>
          <path fill="#9c1116" d="M30.149,21.226l1.538,4.423l-4.423,1.538l-1.538-4.423L30.149,21.226 M30.765,19.953l-6.312,2.195 l2.195,6.312l6.312-2.195L30.765,19.953L30.765,19.953z"></path>
          <g>
            <path fill="#7f9331" d="M21.372 8.446H27.054000000000002V14.128H21.372z" transform="rotate(-19.178 24.213 11.287)"></path>
            <path fill="#657527" d="M25.656,8.306l1.538,4.423l-4.423,1.538l-1.538-4.423L25.656,8.306 M26.272,7.033L19.96,9.228 l2.195,6.312l6.312-2.195L26.272,7.033L26.272,7.033z"></path>
          </g>
          <g>
            <path fill="#66c1a0" d="M18.204,38.5c-1.208,0-2.287-0.767-2.684-1.909L6.025,9.287C5.775,8.569,5.82,7.798,6.151,7.115 c0.33-0.683,0.907-1.196,1.624-1.446c0.303-0.105,0.616-0.159,0.934-0.159c1.208,0,2.286,0.767,2.683,1.909l9.496,27.305 c0.515,1.48-0.271,3.103-1.75,3.617C18.835,38.446,18.521,38.5,18.204,38.5z"></path>
            <path fill="#4e917a" d="M8.709,6.011L8.709,6.011c0.995,0,1.884,0.632,2.211,1.573l9.496,27.305 c0.424,1.219-0.223,2.556-1.442,2.98C18.724,37.956,18.465,38,18.204,38c-0.995,0-1.884-0.632-2.211-1.573L6.497,9.122 C6.292,8.531,6.329,7.896,6.601,7.333C6.873,6.77,7.349,6.347,7.94,6.142C8.189,6.055,8.448,6.011,8.709,6.011 M8.709,5.011 c-0.364,0-0.734,0.06-1.098,0.186c-1.743,0.606-2.665,2.51-2.058,4.253l9.496,27.305c0.48,1.38,1.773,2.245,3.156,2.245 c0.364,0,0.734-0.06,1.098-0.186c1.743-0.606,2.665-2.51,2.058-4.253L11.864,7.256C11.385,5.876,10.092,5.011,8.709,5.011 L8.709,5.011z"></path>
          </g>
          <g>
            <path fill="#852759" d="M12.945 25.865H18.627000000000002V31.546999999999997H12.945z" transform="rotate(-19.178 15.786 28.706)"></path>
            <path fill="#551537" d="M17.229,25.726l1.538,4.423l-4.423,1.538l-1.538-4.423L17.229,25.726 M17.845,24.453l-6.312,2.195 l2.195,6.312l6.312-2.195L17.845,24.453L17.845,24.453z"></path>
          </g>
          <g>
            <path fill="#3ba08d" d="M8.452 12.946H14.135V18.628H8.452z" transform="rotate(-19.172 11.295 15.789)"></path>
            <path fill="#318575" d="M12.736,12.806l1.538,4.423l-4.423,1.538l-1.538-4.423L12.736,12.806 M13.352,11.533L7.04,13.728 l2.195,6.312l6.312-2.195L13.352,11.533L13.352,11.533z"></path>
          </g>
        </svg>
      ),
      color: 'from-[#4A154B] to-[#611F69]'
    }
  ];

  const features = [
    {
      icon: Calendar,
      title: t('home.appointments.features.calendar.title'),
      description: t('home.appointments.features.calendar.description')
    },
    {
      icon: Users,
      title: t('home.appointments.features.candidates.title'),
      description: t('home.appointments.features.candidates.description')
    },
    {
      icon: Video,
      title: t('home.appointments.features.video.title'),
      description: t('home.appointments.features.video.description')
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50 appointments-handwritten-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center appointment-badge-sticker mb-6">
            <Calendar className="h-4 w-4 mr-2" />
            {t('home.appointments.badge')}
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 appointment-hand-title">
            {t('home.appointments.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto appointment-hand-subtitle">
            {t('home.appointments.subtitle')}
          </p>
        </motion.div>

        {/* Main Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto mb-16"
        >
          <div className="appointment-torn-stage">
            <div className="appointment-torn-shadow" aria-hidden="true" />
            <article className="appointment-torn-paper">
              <div className="appointment-torn-content">
              {/* Center Logo */}
              <div className="text-center mb-10">
                <motion.div
                  animate={{ 
                    scale: [1, 1.05, 1],
                    rotate: [0, 2, -2, 0]
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                  className="inline-block"
                >
                  <div className="appointment-center-logo-card">
                    <img
                      src={brandLogo}
                      alt="Candio logo"
                      className="appointment-center-logo-img"
                    />
                  </div>
                </motion.div>
              </div>

              <div className="appointment-network-lines hidden md:block" aria-hidden="true">
                <svg viewBox="0 0 100 86" preserveAspectRatio="none" className="w-full h-full">
                  <path className="appointment-network-path-main" d="M50 6 C40 18, 25 36, 12.5 76" />
                  <path className="appointment-network-path-head" d="M8.8 67.6 L12.5 76 L18.5 69.4" />

                  <path className="appointment-network-path-main" d="M50 6 C47.4 18, 42.8 35, 37.5 76" />
                  <path className="appointment-network-path-head" d="M34 68.6 L37.5 76 L41.8 69.2" />

                  <path className="appointment-network-path-main" d="M50 6 C52.6 18, 57.2 35, 62.5 76" />
                  <path className="appointment-network-path-head" d="M58.2 69.2 L62.5 76 L66 68.6" />

                  <path className="appointment-network-path-main" d="M50 6 C60 18, 75 36, 87.5 76" />
                  <path className="appointment-network-path-head" d="M81.5 69.4 L87.5 76 L91.2 67.6" />
                </svg>
              </div>

              {/* Provider Connections */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12 appointment-provider-grid">
                {providers.map((provider, index) => (
                  <motion.div
                    key={provider.name}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    {/* Provider Card */}
                    <div className={`appointment-provider-card appointment-provider-rot-${index % 4}`}>
                      <div className="appointment-provider-logo">
                        <span className="appointment-provider-icon">{provider.icon}</span>
                      </div>
                      <p className="appointment-provider-name">{provider.name}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="appointment-feature-card"
                    >
                      <Icon className="h-8 w-8 appointment-feature-icon mb-3" />
                      <h3 className="appointment-feature-title mb-2">{feature.title}</h3>
                      <p className="appointment-feature-description">{feature.description}</p>
                    </motion.div>
                  );
                })}
              </div>
              </div>
            </article>
          </div>
        </motion.div>

        {/* How it Works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8 appointment-how-title">
            {t('home.appointments.howItWorks.title')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((step) => (
              <div key={step} className="text-center appointment-step-card">
                <div className="w-12 h-12 appointment-step-badge text-white font-bold text-lg mx-auto mb-4">
                  {step}
                </div>
                <h4 className="appointment-step-title mb-2">
                  {t(`home.appointments.howItWorks.step${step}.title`)}
                </h4>
                <p className="appointment-step-description">
                  {t(`home.appointments.howItWorks.step${step}.description`)}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href="/signup"
            className="appointment-cta-button inline-flex items-center px-8 py-4 rounded-xl transform hover:scale-105 transition-all shadow-lg"
          >
            {t('home.appointments.cta')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default AppointmentSection;
